import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectBuscaOpcao {
  id: number;
  descricao: string;
}

/** Select com busca (digitar filtra, sem acento/caixa); valor do ngModel = id da opção. */
@Component({
  selector: 'app-select-busca',
  templateUrl: './select-busca.component.html',
  styleUrls: ['./select-busca.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectBuscaComponent), multi: true },
  ],
})
export class SelectBuscaComponent implements ControlValueAccessor {
  @Input() placeholder = 'Selecione ou digite para buscar';
  @Input() maxResultados = 100;
  @Input() exibirId = false;

  @Input()
  set opcoes(valor: SelectBuscaOpcao[]) {
    this._opcoes = (valor || []).map((o) => ({ ...o, _busca: this.normalizar(`${o.id} ${o.descricao}`) }));
    this.atualizarTextoSelecionado();
    this.filtrar();
  }
  get opcoes(): SelectBuscaOpcao[] {
    return this._opcoes;
  }

  @ViewChild('inputBusca', { static: true }) inputBusca: ElementRef<HTMLInputElement>;
  @ViewChild('lista', { static: false }) lista: ElementRef<HTMLElement>;

  aberto = false;
  desabilitado = false;
  textoBusca = '';
  filtradas: SelectBuscaOpcao[] = [];
  totalEncontradas = 0;
  indiceAtivo = -1;
  valor: number | null = null;

  private _opcoes: (SelectBuscaOpcao & { _busca: string })[] = [];
  private onChange: (v: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elRef: ElementRef, private cdr: ChangeDetectorRef) {}

  writeValue(v: number | null): void {
    this.valor = v != null && Number(v) > 0 ? Number(v) : null;
    this.atualizarTextoSelecionado();
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.desabilitado = isDisabled;
  }

  get rotuloSelecionado(): string {
    const sel = this._opcoes.find((o) => o.id === this.valor);
    return sel ? this.rotulo(sel) : '';
  }

  rotulo(o: SelectBuscaOpcao): string {
    return this.exibirId ? `${o.id} - ${o.descricao}` : o.descricao;
  }

  abrir(): void {
    if (this.desabilitado || this.aberto) {
      return;
    }
    this.aberto = true;
    this.textoBusca = '';
    this.filtrar();
    this.indiceAtivo = this.filtradas.findIndex((o) => o.id === this.valor);
    setTimeout(() => {
      this.inputBusca.nativeElement.select();
      this.rolarParaAtivo();
    });
  }

  alternar(): void {
    if (this.aberto) {
      this.fechar();
    } else {
      this.abrir();
      this.inputBusca.nativeElement.focus();
    }
  }

  fechar(): void {
    if (!this.aberto) {
      return;
    }
    this.aberto = false;
    this.atualizarTextoSelecionado();
    this.onTouched();
  }

  onDigitar(texto: string): void {
    if (!this.aberto) {
      this.aberto = true;
    }
    this.textoBusca = texto;
    this.filtrar();
    this.indiceAtivo = this.filtradas.length > 0 ? 0 : -1;
  }

  selecionar(o: SelectBuscaOpcao): void {
    this.valor = o.id;
    this.onChange(this.valor);
    this.aberto = false;
    this.atualizarTextoSelecionado();
    this.onTouched();
  }

  limpar(event: Event): void {
    event.stopPropagation();
    this.valor = null;
    this.onChange(null);
    this.textoBusca = '';
    this.filtrar();
    this.atualizarTextoSelecionado();
  }

  onTecla(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.aberto) {
          this.abrir();
          return;
        }
        this.indiceAtivo = Math.min(this.indiceAtivo + 1, this.filtradas.length - 1);
        this.rolarParaAtivo();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.indiceAtivo = Math.max(this.indiceAtivo - 1, 0);
        this.rolarParaAtivo();
        break;
      case 'Enter':
        if (this.aberto && this.indiceAtivo >= 0 && this.filtradas[this.indiceAtivo]) {
          event.preventDefault();
          this.selecionar(this.filtradas[this.indiceAtivo]);
        }
        break;
      case 'Escape':
        this.fechar();
        break;
      case 'Tab':
        this.fechar();
        break;
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onCliqueFora(event: MouseEvent): void {
    if (this.aberto && !this.elRef.nativeElement.contains(event.target)) {
      this.fechar();
      this.cdr.markForCheck();
    }
  }

  trackById(_i: number, o: SelectBuscaOpcao): number {
    return o.id;
  }

  private filtrar(): void {
    const termos = this.normalizar(this.textoBusca).split(' ').filter((t) => t.length > 0);
    const encontradas = termos.length === 0
      ? this._opcoes
      : this._opcoes.filter((o) => termos.every((t) => o._busca.includes(t)));
    this.totalEncontradas = encontradas.length;
    this.filtradas = encontradas.slice(0, this.maxResultados);
  }

  private atualizarTextoSelecionado(): void {
    if (this.aberto) {
      return;
    }
    const sel = this._opcoes.find((o) => o.id === this.valor);
    this.textoBusca = sel ? this.rotulo(sel) : '';
  }

  private rolarParaAtivo(): void {
    setTimeout(() => {
      const el = this.lista?.nativeElement?.querySelector('.ativo') as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  private normalizar(v: string): string {
    return String(v || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }
}
